import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { word } from "@modules/core/utils/i18n"
import { BookTextIcon, DumbbellIcon, LightbulbIcon, ScrollTextIcon, VideoIcon } from "lucide-react"

type AddBlockProps = {
  onAddExerciseBlock: () => void
  onAddCoachNoteBlock: () => void
  onAddAthleteNoteBlock: () => void
  onAddMediaBlock: () => void
  onAddTemplateExercise?: () => void
  className?: string
} & React.ComponentProps<"div">

export function AddBlock({
  onAddExerciseBlock,
  onAddCoachNoteBlock,
  onAddAthleteNoteBlock,
  onAddMediaBlock,
  onAddTemplateExercise,
  className,
  ...props
}: AddBlockProps) {
  return (
    <div className={cn("flex flex-col gap-3 rounded-2xl bg-surface-foreground px-5 py-6", className)} {...props}>
      <div className="flex flex-col items-center">
        <Text className="mb-1" fontStyle="LL">
          {word("sessionForm.blocks.add.title")}
        </Text>
        <Text fontStyle="BS" className="text-textDefault-caption">
          {word("sessionForm.blocks.add.description")}
        </Text>
      </div>

      <div className="grid w-full grid-cols-1 gap-1 sm:grid-cols-3">
        <AddBlockItem onClick={onAddExerciseBlock}>
          <DumbbellIcon color={"currentColor"} />
          <Text fontStyle="LM">{word("sessionForm.blocks.add_workout.button")}</Text>
        </AddBlockItem>
        <AddBlockItem onClick={onAddCoachNoteBlock}>
          <LightbulbIcon />
          <Text fontStyle="LM">{word("sessionForm.blocks.add_note_for_coaches.button")}</Text>
        </AddBlockItem>
        <AddBlockItem onClick={onAddAthleteNoteBlock}>
          <ScrollTextIcon />
          <Text fontStyle="LM">{word("sessionForm.blocks.add_note_for_athletes.button")}</Text>
        </AddBlockItem>
        <AddBlockItem onClick={onAddMediaBlock}>
          <VideoIcon />
          <Text fontStyle="LM">{word("sessionForm.blocks.add_media.button")}</Text>
        </AddBlockItem>
        {onAddTemplateExercise && (
          <AddBlockItem onClick={onAddTemplateExercise}>
            <BookTextIcon />
            <Text fontStyle="LM">{word("global.template.plural", { count: 1 })}</Text>
          </AddBlockItem>
        )}
      </div>
    </div>
  )
}

type AddBlockItemProps = {
  className?: string
  children: React.ReactNode
} & React.ComponentProps<"div">

function AddBlockItem({ className, children, ...props }: AddBlockItemProps) {
  return (
    <div
      className={cn(
        "group/block-item transition-duration-350 flex min-h-19 flex-1 cursor-pointer flex-col items-center gap-3 rounded-[1.25rem] border border-surface-border-gray-subtle bg-interactive-background-neutral-default py-4 text-center text-textOnSurface-muted transition-all",
        "hover:border-surface-border-gray-default hover:bg-interactive-background-neutral-highlighted",
        "[&>svg]:size-5.5 [&>svg]:shrink-0 [&>svg]:rounded-full [&>svg]:bg-interactive-background-neutral-faded [&>svg]:text-textOnSurface-muted [&>svg]:ring-5 [&>svg]:ring-interactive-background-neutral-faded",
        "[&>svg]:group-hover/block-item:bg-interactive-background-neutral-default [&>svg]:group-hover/block-item:ring-interactive-background-neutral-default",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}
